import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { claimGift, giftUpdates, giftsAreFree, managedGifts, offerIsActive, automaticGiftPlan, increasedCaseVariant } from './vyrloom-free-gift-rules.js?v=20261031-performance-1';

class GiftController {
  state = null;
  serial = Promise.resolve();
  pending = new Set();
  internalEvents = new WeakSet();
  busy = false;
  error = '';
  blocked = false;
  initialized = false;
  timer = null;
  recentCaseId = '';
  pendingDecline = false;
  failedAttempt = '';
  pendingExternal = 0;
  latestResponse = null;

  constructor() {
    const onCartEvent = (event) => {
      if (this.internalEvents.has(event)) return;
      const removedGift = event.action === 'remove' && event.lines?.some((line) => this.state?.giftLines.some((gift) => gift.key === line.id && line.quantity === 0));
      const recentCaseId = event.action === 'add' ? event.lines?.at(-1)?.merchandiseId?.split('/').pop() : null;
      this.pendingExternal++;
      this.setBusy(true);
      const promise = Promise.resolve(event.promise).then(async (result) => {
        this.latestResponse = result?.detail?.sections && Array.isArray(result.detail.items)
          ? { items: result.detail.items, item_count: result.detail.itemCount ?? result.cart?.totalQuantity, sections: result.detail.sections }
          : null;
        if (removedGift) this.pendingDecline = true;
        if (recentCaseId) this.recentCaseId = recentCaseId;
        if (result?.cart?.totalQuantity === 0 && this.state) {
          this.pendingDecline = false;
          this.failedAttempt = '';
          if (this.state.declined || this.state.preferredVariant) {
            this.latestResponse = await this.request('update.js', { attributes: { vyrloom_gift_declined: '', vyrloom_gift_model: '' } });
          }
        }
      }).catch(() => {}).finally(() => { this.pendingExternal--; });
      this.pending.add(promise);
      promise.finally(() => {
        this.pending.delete(promise);
        this.schedule();
      });
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, onCartEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, onCartEvent);
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.schedule(); });
    window.addEventListener('focus', () => { if (this.state) this.schedule(); });
    document.addEventListener('submit', (event) => {
      if ((this.blocked || this.busy) && event.submitter?.name === 'checkout') {
        event.preventDefault();
        this.renderAll();
      }
    }, true);
  }

  register(element) {
    const initial = element.readState();
    if (!this.state) this.state = initial;
    this.render(element);
    if (!this.initialized) {
      this.initialized = true;
      if (initial) this.schedule();
    }
  }

  schedule() {
    clearTimeout(this.timer);
    this.setBusy(true);
    this.timer = setTimeout(() => this.run(() => this.reconcile()), 60);
  }

  run(task) {
    this.serial = this.serial.then(async () => {
      while (this.pending.size) await Promise.all([...this.pending]);
      if (!document.querySelector('vyrloom-free-gift')) {
        this.setBusy(false);
        return;
      }
      this.setBusy(true);
      try {
        await task();
      } catch (error) {
        this.error = this.blocked || error.message === 'remove_failed' ? 'remove_failed' : (this.state?.messages[error.message] ? error.message : 'error');
        if (this.error === 'remove_failed') this.blocked = true;
      } finally {
        this.setBusy(false);
      }
    });
    return this.serial;
  }

  get sectionIds() {
    const cartSections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId);
    const giftSections = [...document.querySelectorAll('vyrloom-free-gift')].map((element) => element.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '')).filter(Boolean);
    return [...new Set([...giftSections, ...cartSections])].slice(0, 5);
  }

  get root() {
    return window.Shopify.routes.root;
  }

  async request(path, body) {
    const response = await fetch(this.root + 'cart/' + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: this.sectionIds, sections_url: window.location.pathname }),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors) throw new Error('error');
    return cart;
  }

  async load(response) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionIds.join(','));
    const read = (sections) => {
      for (const html of Object.values(sections || {})) {
        if (!html) continue;
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gift-state]');
        if (node) return JSON.parse(node.textContent);
      }
    };
    let sections = response?.sections;
    let state = read(sections);
    if (!state) {
      const result = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!result.ok) throw new Error('error');
      sections = await result.json();
      state = read(sections);
    }
    let cart = state?.cartSnapshot;
    if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count)) {
      if (Array.isArray(response?.items) && Number.isInteger(response.item_count)) cart = response;
      else {
        const result = await fetch(this.root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!result.ok) throw new Error('error');
        cart = await result.json();
      }
    }
    if (!state && cart.items.length === 0 && this.state) {
      state = {
        ...this.state,
        eligibleQuantity: 0,
        devices: [],
        eligibleCases: [],
        giftLines: [],
        declined: cart.attributes?.vyrloom_gift_declined === '1',
        preferredVariant: cart.attributes?.vyrloom_gift_model || '',
      };
    }
    if (!state) {
      if (managedGifts(cart, this.state?.productId).length) throw new Error('remove_failed');
      throw new Error('error');
    }
    return { cart: { ...cart, sections }, state };
  }

  async sync(cart, state) {
    this.state = state;
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
    this.internalEvents.add(event);
    document.dispatchEvent(event);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'vyrloom-free-gift' },
    });
    await new Promise((resolve) => {
      const timer = setTimeout(resolve, 100);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
      }));
    });
    this.renderAll();
  }

  async reconcile() {
    const response = this.latestResponse;
    this.latestResponse = null;
    let { cart, state } = await this.load(response);
    let attributes = {};
    const update = async (updates = {}) => {
      const result = await this.request('update.js', { updates, ...(Object.keys(attributes).length ? { attributes } : {}) });
      attributes = {};
      ({ cart, state } = await this.load(result));
    };
    const increasedCaseId = increasedCaseVariant(this.state, state, this.recentCaseId);
    if (increasedCaseId) {
      this.recentCaseId = increasedCaseId;
      this.failedAttempt = '';
      if (state.preferredVariant) {
        attributes.vyrloom_gift_model = '';
        state = { ...state, preferredVariant: '' };
      }
    }
    if (this.pendingDecline) {
      this.pendingDecline = false;
      attributes.vyrloom_gift_declined = '1';
      state = { ...state, declined: true };
    }
    if (state.eligibleQuantity === 0 && (state.declined || state.preferredVariant)) {
      attributes = { vyrloom_gift_declined: '', vyrloom_gift_model: '' };
      state = { ...state, declined: false, preferredVariant: '' };
      this.failedAttempt = '';
    }
    this.state = state;
    if (state.eligibleQuantity < state.minimum) this.failedAttempt = '';
    let updates = giftUpdates(cart, state);
    let changed = false;
    let removedPaidGift = false;
    if (Object.keys(updates).length) {
      this.blocked = true;
      await update(updates);
      changed = true;
    }
    if (managedGifts(cart, state.productId).length && !giftsAreFree(cart, state.productId)) {
      this.blocked = true;
      updates = Object.fromEntries(managedGifts(cart, state.productId).map((item) => [item.key, 0]));
      await update(updates);
      this.error = 'discount_unavailable';
      removedPaidGift = true;
      changed = true;
    }
    const plan = automaticGiftPlan(cart, state, { recentCaseId: this.recentCaseId, failedAttempt: this.failedAttempt, rematch: Boolean(increasedCaseId) });
    if (plan.shouldRemove) {
      this.blocked = true;
      updates = Object.fromEntries(managedGifts(cart, state.productId).map((item) => [item.key, 0]));
      await update(updates);
      changed = true;
    }
    if (removedPaidGift) this.failedAttempt = plan.attempt;
    if (plan.shouldAdd && !removedPaidGift) {
      this.failedAttempt = plan.attempt;
      await claimGift({
        productId: state.productId,
        variantId: plan.variantId,
        attributes,
        initial: { cart, state },
        load: (result) => this.load(result),
        write: (path, body) => this.request(path, body),
        onSync: (nextCart, nextState) => this.sync(nextCart, nextState),
      });
      this.failedAttempt = '';
      this.error = '';
      this.blocked = false;
      return;
    }
    if (Object.keys(attributes).length) {
      await update();
      changed = true;
    }
    this.blocked = false;
    this.state = state;
    if (changed) await this.sync(cart, state);
    else this.renderAll();
  }

  claim(variantId, restore = false) {
    if (this.busy) return;
    this.error = '';
    this.setBusy(true);
    this.run(async () => {
      const { cart, state } = await this.load();
      const choice = variantId || automaticGiftPlan({ items: [] }, state, { recentCaseId: this.recentCaseId }).variantId;
      if (!choice) {
        if (restore) {
          const result = await this.request('update.js', { attributes: { vyrloom_gift_declined: '' } });
          const next = await this.load(result);
          await this.sync(next.cart, next.state);
        }
        return;
      }
      await claimGift({
        productId: state.productId,
        variantId: choice,
        attributes: { vyrloom_gift_declined: '', vyrloom_gift_model: String(choice) },
        initial: { cart, state },
        load: (result) => this.load(result),
        write: (path, body) => this.request(path, body),
        onSync: (nextCart, nextState) => this.sync(nextCart, nextState),
      });
      this.failedAttempt = '';
      this.blocked = false;
    });
  }

  setBusy(busy) {
    busy = busy || this.pendingExternal > 0;
    this.busy = busy;
    document.documentElement.toggleAttribute('data-vyrloom-gift-busy', busy);
    for (const checkout of document.querySelectorAll('[name="checkout"]')) {
      if ((busy || this.blocked) && !checkout.disabled) {
        checkout.dataset.giftTemporarilyDisabled = 'true';
        checkout.disabled = true;
      } else if (!busy && !this.blocked && checkout.dataset.giftTemporarilyDisabled) {
        checkout.disabled = false;
        delete checkout.dataset.giftTemporarilyDisabled;
      }
    }
    this.renderAll();
  }

  renderAll() {
    for (const element of document.querySelectorAll('vyrloom-free-gift')) this.render(element);
  }

  render(element) {
    const state = this.state || element.readState();
    if (!state) return;
    const select = element.querySelector('[data-gift-model]');
    const button = element.querySelector('[data-gift-claim]');
    if (!select || !button) return;
    const messages = state.messages;
    const active = offerIsActive(state);
    const unlocked = active && state.eligibleQuantity >= state.minimum;
    const gift = state.giftLines[0];
    const giftIsFree = state.giftLines.length === 1 && gift.quantity === 1 && gift.final_line_price === 0;
    const remaining = Math.max(0, state.minimum - state.eligibleQuantity);
    if (String(element.dataset.selected || '') !== String(gift?.variant_id || '')) {
      select.value = gift ? String(gift.variant_id) : '';
      element.dataset.selected = gift ? String(gift.variant_id) : '';
      delete element.dataset.editing;
    }
    if (!gift && !select.value) select.value = automaticGiftPlan({ items: [] }, state, { recentCaseId: this.recentCaseId }).variantId || '';
    if (gift) select.value = String(gift.variant_id);
    for (const option of select.options) {
      if (!option.value) continue;
      option.disabled = !state.variants.find((variant) => String(variant.id) === option.value)?.available;
    }
    select.disabled = !unlocked || this.busy;
    element.dataset.state = giftIsFree ? 'claimed' : (unlocked ? 'ready' : 'locked');
    element.querySelector('[data-gift-form]').hidden = !unlocked || (!gift && state.declined);
    element.querySelector('[data-gift-product]').hidden = !unlocked || giftIsFree || state.declined;
    const help = element.querySelector('[data-gift-help]');
    help.hidden = !unlocked || state.declined;
    help.textContent = giftIsFree ? (new Set(state.devices).size > 1 ? messages.mixed_models : messages.check_model) : messages.choose_to_add;
    button.hidden = !unlocked || Boolean(gift) || (!state.declined && !this.error);
    button.disabled = !unlocked || this.busy;
    button.textContent = this.busy ? messages.busy : (state.declined ? messages.restore : messages.retry);
    element.querySelector('.vyrloom-gift__icon').textContent = giftIsFree ? '✓' : '✦';
    element.querySelector('[data-gift-heading]').textContent = !active ? messages.inactive : (giftIsFree ? messages.claimed : (unlocked ? (state.declined ? messages.declined : messages.unlocked) : (remaining === 1 ? messages.remaining_one : messages.remaining.replace('[count]', remaining))));
    element.querySelector('.vyrloom-gift__count').textContent = `${Math.min(state.eligibleQuantity, state.minimum)} / ${state.minimum}`;
    element.querySelector('.vyrloom-gift__track > span').style.width = `${Math.min(100, state.eligibleQuantity / state.minimum * 100)}%`;
    element.toggleAttribute('data-unlocked', unlocked);
    element.toggleAttribute('data-error', Boolean(this.error));
    element.setAttribute('aria-busy', String(this.busy));
    const status = element.querySelector('[data-gift-status]');
    status.textContent = this.error ? messages[this.error] : (!state.variants.some((variant) => variant.available) ? messages.unavailable : '');
  }
}

const controller = new GiftController();

class FreeGiftPicker extends HTMLElement {
  connectedCallback() {
    if (!this.querySelector('[data-gift-state]')) return;
    this.abort?.abort();
    this.abort = new AbortController();
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-gift-model]') && event.target.value) controller.claim(event.target.value);
    }, { signal: this.abort.signal });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-gift-claim]')) controller.claim(this.querySelector('[data-gift-model]').value, true);
    }, { signal: this.abort.signal });
    controller.register(this);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  readState() {
    return JSON.parse(this.querySelector('[data-gift-state]').textContent);
  }
}

if (!customElements.get('vyrloom-free-gift')) customElements.define('vyrloom-free-gift', FreeGiftPicker);


